import React from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Landmark, Sparkles, Trophy, Zap } from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  MediaCarousel,
  PodcastCard,
  VideoCard,
} from "../shared/categoryDetailShared";

const easeOut = [0.22, 1, 0.36, 1];

function BettingSectionHead({ eyebrow, title, subtitle }) {
  return (
    <div className="relative mb-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <span className="h-px w-8 bg-gradient-to-r from-orange-500 to-[#FFD221]" aria-hidden />
        <span className="text-[11px] font-black uppercase tracking-[0.28em] text-orange-600">{eyebrow}</span>
      </div>
      <h2 className="mt-3 text-2xl font-black leading-[1.15] tracking-tight text-stone-950 sm:text-3xl lg:text-[2.05rem]">
        {title}
      </h2>
      {subtitle ? <p className="mt-2 max-w-2xl text-sm font-semibold text-stone-600">{subtitle}</p> : null}
    </div>
  );
}

function CheckList({ items, color }) {
  return (
    <ul className="space-y-2.5">
      {items.map((line) => (
        <li key={line} className="flex gap-3 text-sm font-semibold leading-relaxed text-stone-800 sm:text-[0.9375rem]">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color }} strokeWidth={2.2} />
          <span>{line}</span>
        </li>
      ))}
    </ul>
  );
}

function SurfaceCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-orange-200/40 bg-white p-6 shadow-[0_20px_55px_-40px_rgba(234,88,12,0.22)] ring-1 ring-orange-950/[0.04] sm:p-8 ${className}`.trim()}
    >
      {children}
    </div>
  );
}

function PlatformsKickoff({ data }) {
  const k = data.introKickoff;
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: easeOut }}
      className="overflow-hidden rounded-[1.75rem] border border-stone-200/80 bg-white shadow-[0_24px_80px_-40px_rgba(15,23,42,0.14)] sm:rounded-[2rem]"
    >
      <div className="grid lg:grid-cols-2 lg:items-stretch">
        <div className="relative flex flex-col justify-center overflow-hidden px-7 py-10 sm:px-10 sm:py-12 lg:px-12 lg:py-14">
          <div
            className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 rounded-full opacity-[0.055] blur-3xl"
            style={{ background: "#1c1917" }}
            aria-hidden
          />
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-white">
              {k.chipCategory}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-200/80 bg-orange-50/80 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-orange-700">
              <Trophy className="h-3 w-3 shrink-0" />
              {k.chipSecondary}
            </span>
          </div>
          <h2 className="text-[1.65rem] font-black leading-[1.1] tracking-tight text-stone-950 sm:text-[2rem] lg:text-[2.1rem]">
            {k.headline}
          </h2>
          <p className="mt-4 max-w-prose text-[0.9375rem] leading-relaxed text-stone-500">{k.lead}</p>
          <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <div className="rounded-2xl border border-stone-100 bg-stone-50/80 p-4">
              <p className="text-[9px] font-black uppercase tracking-[0.24em] text-orange-600">Live product mix</p>
              <ul className="mt-2.5 space-y-1.5">
                {k.everydayLabels.map((s) => (
                  <li key={s} className="flex items-center gap-2 text-[0.8125rem] font-semibold text-stone-800">
                    <Zap className="h-3 w-3 shrink-0 text-orange-400" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-stone-100 bg-stone-50/80 p-4">
              <p className="text-[9px] font-black uppercase tracking-[0.24em] text-orange-600">Ghana realities</p>
              <ul className="mt-2.5 space-y-1.5">
                {k.ghanaContext.map((s) => (
                  <li key={s} className="flex items-center gap-2 text-[0.8125rem] font-semibold text-stone-800">
                    <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" strokeWidth={2.5} />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-5 text-[0.8rem] leading-relaxed text-stone-400">{k.researchNote}</p>
          <div className="mt-5 flex flex-wrap gap-1.5">
            {k.focusTags.map((tag) => (
              <span
                key={tag}
                className="rounded-lg border border-stone-200 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-600 shadow-sm"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="relative flex min-h-0 flex-col border-t border-stone-100 lg:min-h-full lg:border-l lg:border-t-0">
          <div className="absolute inset-0 bg-gradient-to-br from-stone-950 via-orange-950/90 to-stone-950" />
          <div className="pointer-events-none absolute -right-12 -top-12 h-56 w-56 rounded-full bg-[#FFD221]/15 blur-3xl" aria-hidden />
          <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23fff\' fill-opacity=\'1\' fill-rule=\'evenodd\'%3E%3Cpath d=\'M0 40L40 0H20L0 20M40 40V20L20 40\'/%3E%3C/g%3E%3C/svg%3E")' }} aria-hidden />

          <div className="relative grid flex-1 grid-cols-2 gap-2 p-4 sm:gap-2.5 sm:p-5 lg:p-6">
            {data.galleryImages.slice(0, 4).map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, scale: 0.94 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06, ease: easeOut }}
                className={`relative overflow-hidden rounded-xl ring-1 ring-white/10 ${i === 0 ? "col-span-2 aspect-[16/9] sm:aspect-[2/1]" : "aspect-square"}`}
              >
                <img src={src} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              </motion.div>
            ))}
          </div>

          <div className="relative shrink-0 border-t border-white/[0.07] px-5 py-5 sm:px-8 sm:py-6 lg:px-6">
            <p className="mb-3 text-[9px] font-black uppercase tracking-[0.28em] text-white/40">Signals at a glance</p>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {data.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl border border-white/[0.08] bg-white/[0.06] px-2 py-3 text-center backdrop-blur-sm sm:px-3"
                >
                  <p className="text-base font-black tabular-nums text-[#FFD221] sm:text-lg">{stat.value}</p>
                  <p className="mt-0.5 text-[8px] font-bold uppercase leading-tight tracking-wide text-white/40 sm:text-[9px]">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-center text-[10px] font-medium text-white/28">
              Monograms below are stylised initials — not operator artwork; verify each brand at source.
            </p>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function OperatorCard({ op }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.45, ease: easeOut }}
      className="group relative overflow-hidden rounded-[1.35rem] border border-white/12 p-5 text-white shadow-[0_24px_50px_-28px_rgba(0,0,0,0.55)] ring-1 ring-black/20 transition duration-300 hover:-translate-y-1 hover:ring-[#FFD221]/25"
      style={{ background: op.surface }}
    >
      <div
        className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-40 blur-2xl transition group-hover:opacity-55"
        style={{ background: op.accent }}
        aria-hidden
      />
      <div className="relative flex items-start gap-4">
        <div
          className="flex h-[3.25rem] w-[3.25rem] shrink-0 items-center justify-center rounded-2xl border-2 text-sm font-black tracking-tight shadow-inner backdrop-blur-sm sm:h-14 sm:w-14 sm:text-base"
          style={{
            borderColor: op.accent,
            color: op.accent,
            background: "rgba(0,0,0,0.42)",
            boxShadow: `0 0 0 1px ${op.accent}33`,
          }}
        >
          {op.initials}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-lg font-black tracking-tight">{op.name}</p>
          <p className="mt-1 text-xs font-semibold leading-snug text-white/80">{op.tagline}</p>
        </div>
      </div>
      <div className="relative mt-3 flex flex-wrap gap-1.5">
        {op.pills.map((p) => (
          <span
            key={p}
            className="rounded-full border border-white/18 bg-black/30 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white/95"
          >
            {p}
          </span>
        ))}
      </div>
      <ul className="relative mt-4 space-y-1.5 text-[13px] font-medium leading-snug text-white/88">
        {op.strengths.map((s) => (
          <li key={s} className="flex gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 opacity-80" style={{ color: op.accent }} />
            <span>{s}</span>
          </li>
        ))}
      </ul>
      {op.watchout ? (
        <p className="relative mt-4 rounded-xl border border-amber-400/25 bg-amber-950/35 px-3 py-2 text-[11px] font-semibold leading-relaxed text-amber-100/95">
          ⚠ {op.watchout}
        </p>
      ) : null}
    </motion.div>
  );
}

export const BestBettingPlatformsOverviewTab = ({ data, color }) => (
  <div className="relative space-y-14">
    <PlatformsKickoff data={data} />

    <section>
      <BettingSectionHead eyebrow="Framing" title={data.whatIs.headline} />
      <SurfaceCard>
        <p className="text-sm font-semibold text-stone-700">{data.whatIs.lead}</p>
        <div className="mt-5">
          <CheckList items={data.whatIs.bullets} color={color} />
        </div>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Mindset" title={data.whyCompare.headline} />
      <SurfaceCard>
        <p className="text-sm font-semibold text-stone-700">{data.whyCompare.body}</p>
        <p className="mt-6 text-xs font-black uppercase text-orange-600">{data.whyCompare.checksTitle}</p>
        <div className="mt-2">
          <CheckList items={data.whyCompare.checks} color={color} />
        </div>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Compliance" title={data.licensing.headline} />
      <SurfaceCard className="!border-blue-200/50 !bg-gradient-to-br from-blue-50/40 to-white">
        <div className="flex flex-wrap items-start gap-4">
          <Landmark className="h-10 w-10 shrink-0 text-blue-700" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-stone-800">{data.licensing.lead}</p>
            <p className="mt-5 text-xs font-black uppercase text-stone-600">{data.licensing.supervisesTitle}</p>
            <ul className="mt-2 space-y-1 text-sm font-medium text-stone-800">
              {data.licensing.supervises.map((s) => (
                <li key={s}>· {s}</li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-stone-500">{data.licensing.disclaimer}</p>
          </div>
        </div>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Due diligence" title={data.compareChecklist.headline} subtitle={data.compareChecklist.subtitle} />
      <div className="grid gap-3 sm:grid-cols-2">
        {data.compareChecklist.rows.map((row) => (
          <SurfaceCard key={row.title} className="!p-5">
            <p className="text-sm font-black text-stone-950">{row.title}</p>
            <p className="mt-2 text-sm font-medium text-stone-600">{row.body}</p>
          </SurfaceCard>
        ))}
      </div>
    </section>

    <section>
      <BettingSectionHead
        eyebrow="Landscape"
        title="OPERATORS GHANAIANS OFTEN COMPARE"
        subtitle="Illustrative — not a ranking. Initials are stylised monograms (not trademark logos). Re-verify offers and licensing on each official site."
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.operators.map((op) => (
          <OperatorCard key={op.id} op={op} />
        ))}
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Crash games" title={data.aviatorCrash.headline} />
      <SurfaceCard className="!border-amber-200/60 !bg-amber-50/20">
        <p className="text-sm font-semibold text-stone-800">{data.aviatorCrash.lead}</p>
        <ul className="mt-4 space-y-1 text-sm font-medium text-stone-800">
          {data.aviatorCrash.bullets.map((b) => (
            <li key={b}>· {b}</li>
          ))}
        </ul>
        <Link
          to="/betting-gaming/virtual-games"
          className="mt-6 inline-flex items-center gap-2 text-sm font-black text-orange-700 underline-offset-4 hover:underline"
        >
          {data.aviatorCrash.linkLabel}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Safety" title={data.redFlags.headline} />
      <SurfaceCard className="!border-red-200/60">
        <ul className="space-y-2 text-sm font-bold text-red-950">
          {data.redFlags.items.map((x) => (
            <li key={x}>🚩 {x}</li>
          ))}
        </ul>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Verification" title={data.verifySteps.headline} />
      <div className="space-y-3">
        {data.verifySteps.steps.map((step) => (
          <SurfaceCard key={step.title} className="!p-5">
            <p className="text-sm font-black text-orange-700">{step.title}</p>
            <p className="mt-2 text-sm font-medium text-stone-700">{step.body}</p>
          </SurfaceCard>
        ))}
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="MoMo" title={data.momoSafety.headline} />
      <SurfaceCard>
        <p className="text-sm font-semibold text-stone-700">{data.momoSafety.lead}</p>
        <div className="mt-4">
          <CheckList items={data.momoSafety.tips} color="#059669" />
        </div>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Cash-outs" title={data.withdrawals.headline} />
      <SurfaceCard>
        <p className="text-sm font-semibold text-stone-700">{data.withdrawals.body}</p>
        <ul className="mt-4 space-y-1 text-sm font-medium text-stone-800">
          {data.withdrawals.bullets.map((b) => (
            <li key={b}>· {b}</li>
          ))}
        </ul>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Devices" title={data.appSecurity.headline} />
      <SurfaceCard>
        <div className="mt-1">
          <CheckList items={data.appSecurity.items} color={color} />
        </div>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Editorial" title={data.contentIdeas.headline} />
      <div className="grid gap-4 sm:grid-cols-2">
        {data.contentIdeas.groups.map((g) => (
          <div key={g.title} className="rounded-2xl border border-stone-200 bg-stone-50/80 p-5">
            <p className="text-sm font-black text-stone-950">{g.title}</p>
            <ul className="mt-3 space-y-1 text-sm font-semibold text-stone-700">
              {g.bullets.map((b) => (
                <li key={b}>· {b}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section className="overflow-hidden rounded-[1.75rem] border border-orange-400/30 bg-gradient-to-br from-[#1a1512] via-stone-900 to-orange-950 p-8 text-white shadow-2xl sm:p-10">
      <div className="flex flex-wrap items-center gap-3">
        <Sparkles className="h-8 w-8 text-[#FFD221]" />
        <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#FFD221]">
          Infoboxx funnel
        </span>
      </div>
      <h3 className="mt-5 text-2xl font-black sm:text-3xl">{data.funnel.headline}</h3>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          {[
            { k: "Hook", v: data.funnel.hook },
            { k: "Education", v: data.funnel.education },
            { k: "Trust", v: data.funnel.trust },
          ].map(({ k, v }) => (
            <div key={k} className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-200">{k}</p>
              <p className="mt-2 text-sm font-semibold text-white/92">{v}</p>
            </div>
          ))}
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FFD221]">Conversion</p>
          <ul className="mt-3 space-y-2 text-sm font-bold text-white/90">
            {data.funnel.conversion.map((c) => (
              <li key={c} className="flex gap-2 rounded-xl border border-white/10 bg-black/25 px-3 py-2">
                <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-[#FFD221]" />
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="rounded-[1.75rem] border-2 border-orange-300/60 bg-gradient-to-br from-[#FFD221]/20 via-white to-orange-50/40 p-8 sm:p-10">
      <BettingSectionHead eyebrow="CTAs" title={data.strongCta.headline} />
      <ul className="mt-4 space-y-3">
        {data.strongCta.items.map((item) => (
          <li key={item} className="flex gap-3 text-sm font-black text-stone-900 sm:text-[0.9375rem]">
            <span className="text-orange-600">👉</span>
            {item}
          </li>
        ))}
      </ul>
    </section>

    <section>
      <BettingSectionHead eyebrow="Summary" title={data.takeaway.headline} />
      <div className="rounded-2xl border border-stone-200 bg-gradient-to-b from-white to-stone-50/50 p-6 sm:p-8">
        <ul className="space-y-3 text-sm font-semibold text-stone-700">
          {data.takeaway.bullets.map((p) => (
            <li key={p} className="flex gap-2">
              <span className="text-emerald-600">✔</span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
        <div className="mt-6 rounded-xl border-l-4 border-orange-500 bg-orange-50/50 p-4 text-sm font-semibold text-stone-800">
          👉 {data.takeaway.footnote}
        </div>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Quick answers" title="FAQs" />
      <div className="rounded-2xl border border-orange-100/90 bg-white p-4 shadow-md sm:p-6">
        <div className="space-y-2 divide-y divide-orange-100/80">
          {data.faqs.map((faq) => (
            <div key={faq.q} className="pt-2 first:pt-0">
              <FAQItem q={faq.q} a={faq.a} color={color} />
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export const BestBettingPlatformsArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-12">
    <section>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <BettingSectionHead eyebrow="Library" title="Articles & guides" subtitle={data.funnelTagline} />
        <span className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-black text-orange-900">
          {data.articles.length} guides
        </span>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>
    <section className="overflow-hidden rounded-[1.5rem] border border-orange-200/70 bg-gradient-to-br from-stone-900 to-orange-950 p-8 text-white sm:p-10">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.28em] text-[#FFD221]">{data.editorialPlaybook.leadCtasHeading}</p>
          <h3 className="text-xl font-black sm:text-2xl">{data.primaryCtaHeading}</h3>
          <ul className="mt-4 space-y-2 text-sm text-white/85">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c} className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#FFD221]" />
                {c}
              </li>
            ))}
          </ul>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-[#FFD221] px-8 py-4 text-sm font-black text-black shadow-lg transition hover:brightness-105"
        >
          Register free
          <ArrowRight size={15} aria-hidden />
        </Link>
      </div>
    </section>
  </div>
);

export const BestBettingPlatformsMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;
  return (
    <div className="min-w-0 space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <BettingSectionHead
          eyebrow="Watch & listen"
          title="Videos & podcasts"
          subtitle={data.editorialPlaybook.videos.join(" · ")}
        />
        <span className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-black text-orange-900">{total} featured</span>
      </div>
      {videos.length > 0 ? (
        <section className="min-w-0 rounded-2xl border border-orange-100/90 bg-white/90 p-4 shadow-md sm:p-6">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Betting platform education videos"
            items={videos}
            header={<h3 className="text-lg font-black text-stone-950 sm:text-xl">Videos</h3>}
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}
      {podcasts.length > 0 ? (
        <section className="min-w-0 rounded-2xl border border-orange-100/90 bg-white/90 p-4 shadow-md sm:p-6">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Betting platform podcasts"
            items={podcasts}
            header={<h3 className="text-lg font-black text-stone-950 sm:text-xl">Podcasts</h3>}
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
        </section>
      ) : null}
    </div>
  );
};
